/**
 * Wheel Config Editor (Phase 7.0 - Premium UX)
 * Polaris-grade UI with presets, color picker, guardrails
 */

import { useState, useCallback, useRef } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Select,
  Text,
  Divider,
  Box,
  Banner,
  ButtonGroup,
} from "@shopify/polaris";
import { DeleteIcon, PlusIcon, ColorIcon } from "@shopify/polaris-icons";
import type { WheelSegment, WheelConfig } from "../games/runtime/types";
import {
  COLOR_SCHEMES,
  applyColorScheme,
  generateNiceColor,
  isValidHexColor,
  normalizeWeights,
  type ColorScheme
} from "../utils/colorSchemes";
import { WHEEL_THEME_OPTIONS } from "../games/wheel_of_fortune/themes/themeOptions";

interface WheelConfigEditorProps {
  config: WheelConfig;
  onChange: (config: WheelConfig) => void;
}

export function WheelConfigEditor({ config, onChange }: WheelConfigEditorProps) {
  const [segments, setSegments] = useState<WheelSegment[]>(config.segments);
  const [theme, setTheme] = useState(config.theme || "modern");
  const [spinDuration, setSpinDuration] = useState(String(config.spinDuration || 4000));
  const [ctaText, setCtaText] = useState(config.ctaText || "Spin to Win!");
  const [validationError, setValidationError] = useState<string | null>(null);

  // Update parent when config changes
  const updateConfig = useCallback((updates: Partial<WheelConfig>) => {
    const newConfig = { ...config, ...updates };
    onChange(newConfig);
  }, [config, onChange]);

  // Apply color scheme
  const handleApplyScheme = useCallback((scheme: ColorScheme) => {
    const newSegments = applyColorScheme(segments, scheme);
    setSegments(newSegments);
    updateConfig({ segments: newSegments });
  }, [segments, updateConfig]);

  // Normalize weights
  const handleNormalizeWeights = useCallback(() => {
    const normalized = normalizeWeights(segments);
    setSegments(normalized);
    updateConfig({ segments: normalized });
  }, [segments, updateConfig]);

  // Add segment
  const handleAddSegment = useCallback(() => {
    if (segments.length >= 12) {
      setValidationError("Maximum 12 segments allowed");
      return;
    }

    const newSegment: WheelSegment = {
      outcomeKey: `outcome_${Date.now()}`,
      label: `Segment ${segments.length + 1}`,
      color: generateNiceColor(),
      weight: 1
    };
    const newSegments = [...segments, newSegment];
    setSegments(newSegments);
    updateConfig({ segments: newSegments });
    setValidationError(null);
  }, [segments, updateConfig]);

  // Remove segment
  const handleRemoveSegment = useCallback((index: number) => {
    if (segments.length <= 2) {
      setValidationError("Minimum 2 segments required");
      return;
    }

    const newSegments = segments.filter((_, i) => i !== index);
    setSegments(newSegments);
    updateConfig({ segments: newSegments });
    setValidationError(null);
  }, [segments, updateConfig]);

  // Update segment
  const handleUpdateSegment = useCallback((index: number, field: keyof WheelSegment, value: string | number) => {
    const newSegments = [...segments];
    
    // Validation
    if (field === 'weight') {
      const numValue = typeof value === 'number' ? value : parseInt(value);
      if (numValue < 1 || numValue > 100) {
        setValidationError("Weight must be between 1 and 100");
        return;
      }
      newSegments[index] = { ...newSegments[index], weight: numValue };
    } else if (field === 'color') {
      const colorValue = String(value);
      if (!isValidHexColor(colorValue)) {
        // Don't update if invalid, keep last valid
        return;
      }
      newSegments[index] = { ...newSegments[index], color: colorValue };
    } else if (field === 'label') {
      const strValue = String(value);
      if (strValue.length > 30) {
        setValidationError("Label max 30 characters");
        return;
      }
      newSegments[index] = { ...newSegments[index], label: strValue };
    } else if (field === 'outcomeKey') {
      newSegments[index] = { ...newSegments[index], outcomeKey: String(value) };
    }

    setSegments(newSegments);
    updateConfig({ segments: newSegments });
    setValidationError(null);
  }, [segments, updateConfig]);

  return (
    <BlockStack gap="400">
      {/* Validation Banner */}
      {validationError && (
        <Banner tone="warning" onDismiss={() => setValidationError(null)}>
          {validationError}
        </Banner>
      )}

      {/* Theme & Settings */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            Wheel Settings
          </Text>
          
          <Select
            label="Theme"
            value={theme}
            options={WHEEL_THEME_OPTIONS}
            onChange={(value) => {
              setTheme(value);
              updateConfig({ theme: value });
            }}
            helpText="Visual style of the wheel"
          />

          <TextField
            label="Spin Duration"
            type="number"
            value={spinDuration}
            onChange={(value) => {
              setSpinDuration(value);
              const duration = parseInt(value) || 4000;
              if (duration >= 1000 && duration <= 10000) {
                updateConfig({ spinDuration: duration });
              }
            }}
            suffix="ms"
            autoComplete="off"
            min="1000"
            max="10000"
            helpText="How long the wheel spins (1-10 seconds)"
          />

          <TextField
            label="Button Text"
            value={ctaText}
            onChange={(value) => {
              setCtaText(value);
              updateConfig({ ctaText: value });
            }}
            autoComplete="off"
            maxLength={20}
            helpText="Text shown on center button"
          />
        </BlockStack>
      </Card>

      {/* Color Schemes */}
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            Color Schemes
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Apply preset color palettes to all segments
          </Text>

          <InlineStack gap="200" wrap>
            {COLOR_SCHEMES.map((scheme) => (
              <Button
                key={scheme.name}
                onClick={() => handleApplyScheme(scheme)}
                size="slim"
              >
                {scheme.name}
              </Button>
            ))}
          </InlineStack>
        </BlockStack>
      </Card>

      {/* Segments */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd" fontWeight="semibold">
                Segments ({segments.length}/12)
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Configure wheel segments and probabilities
              </Text>
            </BlockStack>

            <ButtonGroup>
              <Button
                icon={PlusIcon}
                onClick={handleAddSegment}
                disabled={segments.length >= 12}
              >
                Add
              </Button>
              <Button
                onClick={handleNormalizeWeights}
                size="slim"
              >
                Normalize
              </Button>
            </ButtonGroup>
          </InlineStack>

          <Divider />

          {segments.map((segment, index) => (
            <SegmentEditor
              key={index}
              segment={segment}
              index={index}
              canDelete={segments.length > 2}
              onUpdate={handleUpdateSegment}
              onDelete={handleRemoveSegment}
            />
          ))}

          {segments.length < 2 && (
            <Banner tone="warning">
              Minimum 2 segments required
            </Banner>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

/**
 * Individual Segment Editor
 */
interface SegmentEditorProps {
  segment: WheelSegment;
  index: number;
  canDelete: boolean;
  onUpdate: (index: number, field: keyof WheelSegment, value: string | number) => void;
  onDelete: (index: number) => void;
}

function SegmentEditor({ segment, index, canDelete, onUpdate, onDelete }: SegmentEditorProps) {
  const colorPickerRef = useRef<HTMLInputElement>(null);

  return (
    <Box padding="400" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingSm" fontWeight="medium">
            Segment {index + 1}
          </Text>
          <Button
            icon={DeleteIcon}
            variant="plain"
            tone="critical"
            onClick={() => onDelete(index)}
            disabled={!canDelete}
            accessibilityLabel="Delete segment"
          />
        </InlineStack>

        <TextField
          label="Outcome Key"
          value={segment.outcomeKey}
          onChange={(value) => onUpdate(index, 'outcomeKey', value)}
          autoComplete="off"
          helpText="Must match reward policy outcome"
        />

        <TextField
          label="Label"
          value={segment.label}
          onChange={(value) => onUpdate(index, 'label', value)}
          autoComplete="off"
          maxLength={30}
          showCharacterCount
          helpText="Text displayed on segment"
        />

        <InlineStack gap="400" blockAlign="end">
          <Box minWidth="120px">
            <TextField
              label="Weight"
              type="number"
              value={String(segment.weight)}
              onChange={(value) => onUpdate(index, 'weight', parseInt(value) || 1)}
              autoComplete="off"
              min={1}
              max={100}
              helpText="Relative size"
            />
          </Box>

          <div>
            <Text as="p" variant="bodyMd" fontWeight="medium">
              Color
            </Text>
            <InlineStack gap="200" blockAlign="center">
              {/* Color swatch button */}
              <button
                type="button"
                onClick={() => colorPickerRef.current?.click()}
                style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: segment.color,
                  border: '2px solid #e1e3e5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                aria-label="Pick color"
              />

              {/* Hidden native color picker */}
              <input
                ref={colorPickerRef}
                type="color"
                value={segment.color}
                onChange={(e) => onUpdate(index, 'color', e.target.value.toUpperCase())}
                style={{ display: 'none' }}
              />

              {/* Hex input */}
              <Box minWidth="110px">
                <TextField
                  value={segment.color}
                  onChange={(value) => {
                    const formatted = value.startsWith('#') ? value.toUpperCase() : `#${value.toUpperCase()}`;
                    onUpdate(index, 'color', formatted);
                  }}
                  autoComplete="off"
                  placeholder="#4ECDC4"
                  label=""
                />
              </Box>

              {/* Random color button */}
              <Button
                icon={ColorIcon}
                onClick={() => onUpdate(index, 'color', generateNiceColor())}
                accessibilityLabel="Random color"
              />
            </InlineStack>
          </div>
        </InlineStack>

      </BlockStack>
    </Box>
  );
}
